(() => {
  if (customElements.get('personalized-products')) return;
  class PersonalizedProducts extends HTMLElement {
    connectedCallback() {
      // Async loading bypasses unrelated deferred apps. A second shelf may
      // still be parsing when the first registers this shared element.
      if (!this.querySelector('[data-dots]')) {
        this.markupObserver?.disconnect();
        this.markupObserver = new MutationObserver(() => {
          if (!this.querySelector('[data-dots]')) return;
          this.markupObserver.disconnect();
          if (this.isConnected) this.connectedCallback();
        });
        this.markupObserver.observe(this, { childList: true, subtree: true });
        return;
      }
      this.track = this.querySelector('[data-track]');
      this.heading = this.querySelector('[data-heading]');
      this.subtitle = this.querySelector('[data-subtitle]');
      this.status = this.querySelector('[data-status]');
      this.retry = this.querySelector('[data-retry]');
      this.navigation = this.querySelector('[data-navigation]');
      this.dots = this.querySelector('[data-dots]');
      this.viewAll = this.querySelector('[data-view-all]');
      this.limit = Math.max(6, Math.min(24, Number(this.dataset.limit) || 12));
      this.fallback ??= [...this.track.children].map(node => {
        const clone = node.cloneNode(true);
        // Native quick-add modals may already have moved to document.body.
        // Retain their markup so restoring the initial collection stays usable.
        clone.querySelectorAll('modal-opener[data-modal]').forEach(opener => {
          const id = opener.dataset.modal.slice(1);
          const modal = document.getElementById(id);
          if (!modal) return;
          modal.dataset.personalizedOwner = this.id;
          if (![...clone.querySelectorAll('[id]')].some(element => element.id === id)) clone.append(modal.cloneNode(true));
        });
        clone.querySelectorAll('quick-add-modal').forEach(modal => { modal.dataset.personalizedOwner = this.id; });
        return clone;
      });
      this.fallbackTitle ??= this.heading.textContent;
      this.renderedCards = new Map();
      this.signature = null;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.querySelector('[data-prev]').addEventListener('click', () => this.move(-1), options);
      this.querySelector('[data-next]').addEventListener('click', () => this.move(1), options);
      this.retry.addEventListener('click', () => this.load(), options);
      this.track.addEventListener('scroll', () => this.updateNavigation(), { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || this.dataset.layout === 'grid') return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault(); this.move(event.key === 'ArrowRight' ? 1 : -1);
        }
      }, options);
      document.addEventListener('homedock:interests-changed', () => this.load(), options);
      this.resize = new ResizeObserver(() => this.updateNavigation());
      this.resize.observe(this.track);
      this.updateNavigation();
      // The global tracker initializes before the shelf, independently of cookies.
      if (window.HomedockProductInterests) window.HomedockProductInterests.ready.then(() => { if (this.isConnected) this.load(); });
    }

    disconnectedCallback() {
      this.markupObserver?.disconnect();
      this.events?.abort(); this.request?.abort(); this.resize?.disconnect();
      this.clearModals();
    }

    clearModals() {
      document.querySelectorAll('quick-add-modal[data-personalized-owner]').forEach(modal => {
        if (modal.dataset.personalizedOwner !== this.id) return;
        if (modal.hasAttribute('open')) modal.hide?.();
        modal.remove();
      });
    }

    urlFor(group, source) {
      const override = group.key === 'dining-sets' ? this.dataset.diningCollection
        : group.key === 'chairs-4' ? this.dataset.chairsFourCollection
          : group.key === 'wardrobes' ? this.dataset.wardrobeCollection : '';
      const collection = source === undefined ? override || group.collection : source;
      const url = new URL(collection || this.dataset.searchUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid catalog origin');
      const catalogPath = collection ? /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[a-z0-9][a-z0-9-]*\/?$/i
        : /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i;
      if (!catalogPath.test(url.pathname)) throw new Error('Invalid catalog path');
      if (!collection) {
        url.searchParams.set('q', group.query); url.searchParams.set('type', 'product');
        url.searchParams.set('options[unavailable_products]', 'hide');
        url.searchParams.set('sort_by', 'relevance');
      }
      const range = this.dataset.priceMatching !== 'false' && window.HomedockProductInterests.priceRange(group, this.dataset.priceMargin);
      if (range) {
        url.searchParams.set('filter.v.price.gte', (range.min / 100).toFixed(2));
        if (group.price_basis !== 'display') url.searchParams.set('filter.v.price.lte', (range.max / 100).toFixed(2));
        // Different individual PIX discounts make a catalog-price ceiling unsafe.
        // Scan from the lower bound; the rendered-price check enforces both ends.
        else if (collection) url.searchParams.set('sort_by', 'price-ascending');
      }
      url.searchParams.set('section_id', 'personalized-products-feed');
      return url;
    }

    relatedGroups(groups) {
      const api = window.HomedockProductInterests;
      const nodes = [...this.querySelectorAll('[data-category-node]')];
      const seen = new Set(groups.map(group => this.urlFor(group).pathname));
      const related = [];
      const campaign = /\b(todos|todas|all|home|novidades|lancamentos|destaques|ofertas|promocoes|outlet|black|sale|descontos|vendidos|favoritos)\b/;
      for (const group of groups) {
        const path = this.urlFor(group).pathname;
        const matches = nodes.filter(node => node.dataset.collection === path
          || api.normalize(node.dataset.title) === api.normalize(group.label));
        for (const node of matches) {
          const parent = node.parentElement.closest('[data-category-node]') || node;
          if (campaign.test(api.normalize(parent.dataset.title))) continue;
          // Stay inside the merchant's nearest category branch. A bedroom
          // shortage can use bedroom furniture, never a different department.
          for (const candidate of [parent, ...parent.querySelectorAll('[data-category-node]')]) {
            const collection = candidate.dataset.collection;
            if (!collection || seen.has(collection) || campaign.test(api.normalize(candidate.dataset.title))) continue;
            seen.add(collection);
            related.push({ key: 'collection:' + collection.split('/').filter(Boolean).pop(),
              label: candidate.dataset.title, collection, query: '', weight: group.weight, related: true });
          }
        }
      }
      return related.slice(0, 4);
    }

    async pool(group, signal, excluded, onProgress = () => {}, limit = this.limit) {
      const api = window.HomedockProductInterests;
      const sources = [this.urlFor(group), ...(Array.isArray(group.alternatives) ? group.alternatives.slice(0, 2).map(source => this.urlFor(group, source)) : [])];
      if (group.query) sources.push(this.urlFor(group, ''));
      const urls = [...new Map(sources.map(url => [url.href, url])).values()];
      const cards = [], seen = new Set(excluded);
      let succeeded = false, failure;
      const priceMatching = this.dataset.priceMatching !== 'false' && api.priceRange(group, this.dataset.priceMargin);
      const phases = [urls];
      if (priceMatching) phases.push(urls.map(source => {
        const url = new URL(source);
        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
        if (url.searchParams.get('sort_by') === 'price-ascending') url.searchParams.delete('sort_by');
        return url;
      }));
      // First exhaust the ticket band, then fill from the same category.
      // Each phase shares a bounded four-page budget across its sources.
      for (const [phase, phaseUrls] of phases.entries()) {
        let requests = 0;
        for (const source of phaseUrls) {
          if (cards.length >= limit || requests >= 4) break;
          let url = source;
          const catalogPath = source.pathname;
          try {
            for (let page = 0; url && page < 3 && requests < 4 && cards.length < limit; page++) {
              requests++;
              const response = await fetch(url, { signal, credentials: 'same-origin' });
              if (!response.ok) throw new Error('Catalog unavailable');
              const html = new DOMParser().parseFromString(await response.text(), 'text/html');
              const feed = html.querySelector('[data-personalized-feed]');
              if (!feed) throw new Error('Catalog section unavailable');
              succeeded = true;
              for (const template of feed.querySelectorAll('template[data-personalized-card]')) {
                let product;
                try { product = JSON.parse(template.dataset.product); } catch { continue; }
                const id = String(product.id);
                const inPriceRange = !priceMatching || api.matchesPrice(product, group, this.dataset.priceMargin);
                if (seen.has(id) || !api.matches(product, group) || (!phase && !inPriceRange)) continue;
                seen.add(id); cards.push({ id, template, inPriceRange, priceDistance: Math.abs((product.price || 0) - (group.price || 0)) });
              }
              if (phase) cards.sort((a, b) => Number(b.inPriceRange) - Number(a.inPriceRange) || a.priceDistance - b.priceDistance);
              onProgress({ ...group, cards: [...cards] });
              url = feed.dataset.next ? new URL(feed.dataset.next, url) : null;
              if (url) {
                if (url.origin !== location.origin || url.pathname !== catalogPath) throw new Error('Invalid catalog pagination');
                url.searchParams.set('section_id', 'personalized-products-feed');
              }
            }
          } catch (error) {
            if (signal.aborted) {
              if (cards.length) return { ...group, cards };
              throw error;
            }
            failure = error;
          }
        }
      }
      if (failure && (!succeeded || !cards.length)) throw failure;
      return { ...group, cards };
    }

    async recentCard(item, signal) {
      if (!item?.url) return null;
      const url = new URL(item.url, location.origin);
      if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(url.pathname)) return null;
      url.searchParams.set('section_id', 'personalized-products-feed');
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) return null;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      for (const template of html.querySelectorAll('[data-personalized-feed] template[data-personalized-card]')) {
        try {
          if (String(JSON.parse(template.dataset.product).id) === item.id) return { id: item.id, template };
        } catch { /* Ignore invalid catalog markup. */ }
      }
      return null;
    }

    updateViewAll(group) {
      if (!this.viewAll) return;
      let destination = this.dataset.viewAllUrl || this.dataset.fallbackUrl;
      if (!this.dataset.viewAllUrl && group) {
        const url = this.urlFor(group);
        url.searchParams.delete('section_id');
        url.searchParams.delete('filter.v.price.gte');
        url.searchParams.delete('filter.v.price.lte');
        if (url.searchParams.get('sort_by') === 'price-ascending') url.searchParams.delete('sort_by');
        destination = url.href;
      }
      this.viewAll.hidden = !destination;
      if (destination) this.viewAll.href = destination;
      else this.viewAll.removeAttribute('href');
    }

    restore() {
      this.clearModals();
      this.renderedCards.clear();
      this.track.replaceChildren(...this.fallback.map(node => node.cloneNode(true)));
      this.heading.textContent = this.fallbackTitle;
      this.subtitle.hidden = true; this.status.hidden = true; this.retry.hidden = true;
      this.hidden = !this.fallback.length && !window.Shopify?.designMode;
      this.updateViewAll();
      this.track.scrollLeft = 0; this.updateNavigation();
    }

    cacheKey() { return 'homedock_personalized_shelf_v3:' + this.id; }

    clearCache() {
      try { sessionStorage.removeItem(this.cacheKey()); } catch { /* Storage is optional. */ }
    }

    readCache(signature) {
      try {
        const cached = JSON.parse(sessionStorage.getItem(this.cacheKey()) || 'null');
        if (!cached || cached.signature !== signature || !Number.isFinite(cached.at)
          || Date.now() < cached.at || Date.now() - cached.at >= 60000
          || !Array.isArray(cached.cards) || cached.cards.length < 6 || cached.cards.length > this.limit) return null;
        const cards = cached.cards.map(card => ({ id: card.id,
          template: new DOMParser().parseFromString(card.html, 'text/html').querySelector('template[data-personalized-card]') }));
        if (cards.some(card => !card.template || !/^\d+$/.test(card.id)
          || String(JSON.parse(card.template.dataset.product).id) !== card.id)) return null;
        if (cached.group) this.urlFor(cached.group);
        return { cards, group: cached.group };
      } catch { return null; }
    }

    writeCache(signature, cards, group) {
      try {
        sessionStorage.setItem(this.cacheKey(), JSON.stringify({ signature, at: Date.now(), group,
          cards: cards.map(card => ({ id: card.id, html: card.template.outerHTML })) }));
      } catch { /* Quota limits and blocked storage must not delay suggestions. */ }
    }

    renderCards(cards, group) {
      const next = new Map();
      const scrollLeft = this.track.scrollLeft;
      const removeModals = entry => entry.modalIds.forEach(id => {
        const modal = document.getElementById(id);
        if (modal?.hasAttribute('open')) modal.hide?.();
        modal?.remove();
      });
      if (!this.renderedCards.size) { this.clearModals(); this.track.replaceChildren(); }
      cards.forEach((card, index) => {
        let entry = this.renderedCards.get(card.id);
        const source = card.template.innerHTML;
        const active = entry && (entry.node.contains(document.activeElement)
          || entry.modalIds.some(id => document.getElementById(id)?.hasAttribute('open')));
        if (!entry || (entry.source !== source && !active)) {
          const content = card.template.content.cloneNode(true);
          // A feed can serve several shelves: keep every card's ID references local.
          const ids = new Map();
          content.querySelectorAll('[id]').forEach(node => {
            const previous = node.id; node.id = previous + '-' + this.id; ids.set(previous, node.id);
          });
          content.querySelectorAll('[for], [form], [aria-labelledby], [aria-describedby], [aria-controls]').forEach(node => {
            ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls'].forEach(attribute => {
              if (node.hasAttribute(attribute)) node.setAttribute(attribute, node.getAttribute(attribute).split(' ').map(id => ids.get(id) || id).join(' '));
            });
          });
          content.querySelectorAll('[data-modal], [href^="#"]').forEach(node => {
            ['data-modal', 'href'].forEach(attribute => {
              const selector = node.getAttribute(attribute);
              if (selector?.startsWith('#') && ids.has(selector.slice(1))) node.setAttribute(attribute, '#' + ids.get(selector.slice(1)));
            });
          });
          const modalIds = [...content.querySelectorAll('quick-add-modal')].map(modal => {
            modal.dataset.personalizedOwner = this.id; return modal.id;
          });
          if (entry) { removeModals(entry); entry.node.remove(); }
          entry = { node: content.firstElementChild, source, modalIds };
        }
        const position = this.track.children[index];
        if (position !== entry.node) this.track.insertBefore(entry.node, position || null);
        next.set(card.id, entry);
      });
      this.renderedCards.forEach((entry, id) => {
        if (!next.has(id)) { removeModals(entry); entry.node.remove(); }
      });
      this.renderedCards = next;
      this.heading.textContent = this.dataset.title;
      this.subtitle.textContent = this.dataset.subtitle;
      this.subtitle.hidden = !this.dataset.subtitle;
      this.status.hidden = true; this.retry.hidden = true; this.hidden = false;
      // The first visible row must not wait for native lazy-image discovery.
      const visible = this.columns();
      [...this.track.children].slice(0, visible).forEach(node => {
        const image = node.querySelector('img');
        if (image) image.loading = 'eager';
      });
      this.updateViewAll(group);
      this.track.scrollLeft = scrollLeft; this.updateNavigation();
    }

    async load() {
      const api = window.HomedockProductInterests;
      if (!api) return;
      const history = api.read();
      const groups = api.groups(history, Math.max(1, Math.min(3, Number(this.dataset.interestLimit) || 3)));
      const signature = JSON.stringify([api.allowed(), history, this.dataset, api.current()?.id, document.documentElement.lang]);
      if (signature === this.signature) return;
      this.signature = signature;
      this.request?.abort();
      this.removeAttribute('aria-busy');
      if (!api.allowed() || !groups.length || window.Shopify?.designMode) { this.clearCache(); this.restore(); return; }
      const request = this.request = new AbortController();
      const signal = request.signal;
      const timeout = setTimeout(() => request.abort(), 12000);
      this.setAttribute('aria-busy', 'true');
      // Keep existing cards visible during refreshes. An empty shelf stays
      // hidden until the heading, products and navigation can appear together.
      const excluded = new Set();
      if (api.current()) excluded.add(String(api.current().id));
      const latestProduct = history.find(item => item.kind !== 'collection');
      const pools = new Array(groups.length);
      const cached = this.readCache(signature);
      const visible = this.columns();
      let recent, recentReady = false, primaryReady = false, shown = false;
      let leading = [];
      if (cached) {
        this.renderCards(cached.cards, cached.group);
        leading = cached.cards.slice(0, visible).map(card => card.id);
        shown = true;
      }
      const publish = (complete = false) => {
        if (!this.isConnected || this.request !== request || !api.allowed()) return false;
        // A warm shelf stays intact while its current catalog data is refreshed.
        if (cached && !complete) return false;
        const available = pools.filter(Boolean);
        const candidates = available.flatMap(pool => pool.cards);
        const pinned = recent || candidates.find(card => card.id === latestProduct?.id);
        if (!complete && (!primaryReady || (!recentReady && !pinned))) return false;
        const byId = new Map(candidates.map(card => [card.id, card]));
        if (pinned) byId.set(pinned.id, pinned);
        // Once someone starts browsing, retain the cards they are interacting
        // with as slower categories finish; do not shuffle their carousel.
        if (this.track.scrollLeft > 2 || this.track.contains(document.activeElement)
          || [...this.renderedCards.values()].some(entry => entry.modalIds.some(id => document.getElementById(id)?.hasAttribute('open')))) {
          leading = [...this.renderedCards.keys()];
        }
        const fixed = leading.filter(id => id !== pinned?.id).map(id => byId.get(id)).filter(Boolean);
        const first = [...(pinned ? [pinned] : []), ...fixed];
        const viewed = history.filter(item => item.kind !== 'collection').map(item => item.id);
        const select = pools => api.select(pools, this.limit - first.length,
          [...excluded, ...first.map(card => card.id)], viewed, this.dataset.excludeViewed === 'true');
        const direct = available.filter(pool => !pool.related);
        first.push(...select(direct.map(pool => ({ ...pool, cards: pool.cards.filter(card => card.inPriceRange !== false) }))));
        first.push(...select(direct));
        const cards = [...first, ...select(available.filter(pool => pool.related))];
        // Never enlarge a short selection. Only complete desktop rows are
        // published after the related-category sources have been exhausted.
        if (!cards.length || cards.length < (complete ? 6 : visible)) return false;
        if (!leading.length) leading = cards.slice(0, visible).map(card => card.id);
        const group = available.find(pool => pool.cards.length);
        this.renderCards(cards, group);
        if (!shown || complete) this.writeCache(signature, cards, group && { ...group, cards: undefined });
        shown = true;
        return true;
      };
      try {
        const [results] = await Promise.all([
          Promise.allSettled(groups.map((group, index) => this.pool(group, signal, excluded, pool => {
            pools[index] = pool;
            if (index === 0) primaryReady = true;
            publish();
          }).then(pool => {
            pools[index] = pool;
            if (index === 0) primaryReady = true;
            publish(); return pool;
          }, error => {
            if (index === 0) primaryReady = true;
            publish(); throw error;
          }))),
          this.recentCard(latestProduct, signal).catch(() => null).then(card => {
            recent = card; recentReady = true; publish();
          })
        ]);
        if (!this.isConnected || this.request !== request) return;
        if (!api.allowed()) { this.restore(); return; }
        const selected = new Set([...(recent ? [recent.id] : []), ...pools.filter(Boolean).flatMap(pool => pool.cards.map(card => card.id))]);
        if (selected.size < this.limit && !signal.aborted) {
          for (const group of this.relatedGroups(groups)) {
            if (selected.size >= this.limit || signal.aborted) break;
            try {
              const pool = await this.pool(group, signal, new Set([...excluded, ...selected]), () => {}, this.limit - selected.size);
              pools.push(pool);
              pool.cards.forEach(card => selected.add(card.id));
              publish();
            } catch { /* Another related category may still complete the row. */ }
          }
        }
        if (!this.isConnected || this.request !== request) return;
        if (!publish(true)) {
          if (results.some(result => result.status === 'rejected')) throw new Error('Suggestions unavailable');
          this.clearCache(); this.clearModals(); this.renderedCards.clear();
          this.track.replaceChildren(); this.hidden = true;
        }
      } catch {
        if (this.request !== request || !this.isConnected) return;
        this.signature = null;
        if (shown) return;
        this.restore();
        // Keep an initial collection usable during outages. With no collection,
        // provide a compact retry instead of leaving an endless loader.
        if (!this.fallback.length && api.allowed()) {
          this.hidden = false; this.status.textContent = 'Não foi possível carregar as sugestões.';
          this.status.hidden = false; this.retry.hidden = false;
        }
      } finally {
        clearTimeout(timeout);
        if (this.request === request) this.removeAttribute('aria-busy');
      }
    }

    move(direction) {
      this.track.scrollBy({ left: direction * this.pageStep(),
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    gap() { return parseFloat(getComputedStyle(this.track).columnGap) || 0; }
    columns() { return matchMedia('(max-width: 749px)').matches ? 2 : 6; }
    pageStep() {
      const card = this.track.firstElementChild;
      return card ? (card.getBoundingClientRect().width + this.gap()) * this.columns() : this.track.clientWidth + this.gap();
    }
    updateNavigation() {
      const grid = this.dataset.layout === 'grid';
      const width = this.track.clientWidth;
      const max = this.track.scrollWidth - width;
      this.navigation.hidden = grid || max <= 2;
      this.querySelector('[data-prev]').disabled = this.track.scrollLeft < 2;
      this.querySelector('[data-next]').disabled = this.track.scrollLeft >= max - 2;
      const pages = width ? Math.max(1, Math.ceil(this.track.children.length / this.columns())) : 1;
      this.dots.hidden = grid || pages <= 1;
      if (this.dots.children.length !== pages) {
        this.dots.replaceChildren();
        for (let index = 0; index < pages; index++) {
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'personalized-products__dot';
          button.setAttribute('aria-label', `Ir para a página ${index + 1} de ${pages}`);
          button.setAttribute('aria-controls', this.track.id);
          button.addEventListener('click', () => this.track.scrollTo({ left: index * this.pageStep(),
            behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' }));
          this.dots.append(button);
        }
      }
      const current = this.track.scrollLeft >= max - 2 ? pages - 1 : Math.round(this.track.scrollLeft / this.pageStep());
      [...this.dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
    }
  }
  customElements.define('personalized-products', PersonalizedProducts);
})();
